import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { FitBadge, ScoreBars, ScoreRadar } from '../components/hikari/ScoreBreakdown';
import ProjectMedia from '../components/hikari/ProjectMedia';
import {
  ArrowLeft, ArrowRight, Sparkle, Lightbulb, Target, Warning, ChartBar, ShieldCheck,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
// Plain axios (no auth interceptor) so the sandbox truly works pre-signup.
const sb = axios.create({ baseURL: `${API}/api` });

const SIGNAL_STYLE = {
  skill: 'bg-primary text-primary-foreground',
  specialization: 'bg-foreground text-background',
  location: 'bg-accent text-foreground',
};

const Sandbox = () => {
  const [overview, setOverview] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [why, setWhy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    sb.get('/sandbox/overview')
      .then((r) => {
        setOverview(r.data);
        const first = r.data?.ranked?.[0]?.candidate?.id;
        if (first) setActiveId(first);
      })
      .catch(() => setError('Sandbox unavailable.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeId) return;
    setWhy(null);
    try { window.__hikariSandboxActiveCandidate = activeId; } catch { /* noop */ }
    sb.get(`/sandbox/candidate-why/${activeId}`).then((r) => setWhy(r.data)).catch(() => {});
  }, [activeId]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-20 mono text-sm text-muted-foreground">
        Loading sandbox…
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-20 text-center">
        <div className="label-eyebrow text-primary mb-2">SANDBOX</div>
        <h1 className="font-display font-black text-3xl tracking-tighter mb-4">
          {error || "We couldn't load the sandbox."}
        </h1>
        <Link to="/"><Button className="rounded-none">Back to home</Button></Link>
      </div>
    );
  }

  const job = overview.job;
  const active = overview.ranked.find((r) => r.candidate.id === activeId);

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Top banner */}
      <div className="bg-foreground text-background border-b border-border" data-testid="sandbox-banner">
        <div className="max-w-[1400px] mx-auto px-6 py-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Sparkle size={20} weight="fill" className="text-accent" />
            <div>
              <div className="label-eyebrow text-accent mb-1">
                SANDBOX · NO SIGNUP NEEDED
              </div>
              <div className="font-display font-black text-xl lg:text-2xl tracking-tighter">
                See how Hikari ranks candidates in 60 seconds.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button
                variant="outline"
                size="sm"
                className="rounded-none bg-transparent border-background/40 text-background hover:bg-background hover:text-foreground"
                data-testid="sandbox-back-home"
              >
                <ArrowLeft size={14} className="mr-1" /> Back to home
              </Button>
            </Link>
            <Link to="/register">
              <Button
                size="sm"
                className="rounded-none bg-accent text-foreground hover:bg-accent/90"
                data-testid="sandbox-register-cta"
              >
                Post a real role <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        {/* Job context */}
        <div className="bg-white border border-border p-6 mb-0" data-testid="sandbox-job">
          <div className="label-eyebrow text-primary mb-2">DEMO ROLE</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-2">
            {job.title}
          </h1>
          <div className="mono text-xs text-muted-foreground mb-3">
            {[job.location, job.work_mode, job.employment_type].filter(Boolean).join(' · ')}
          </div>
          {job.required_skills?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {job.required_skills.map((s) => (
                <span key={s} className="label-eyebrow bg-secondary px-2 py-0.5">{s}</span>
              ))}
            </div>
          )}
        </div>

        {/* Ranked list + Why panel */}
        <div className="grid lg:grid-cols-12 gap-0 grid-skeleton">
          {/* Left: ranked candidates */}
          <div className="lg:col-span-4 bg-white p-6" data-testid="sandbox-ranked">
            <div className="label-eyebrow text-muted-foreground mb-3">
              RANKED CANDIDATES · {overview.ranked.length}
            </div>
            <ul className="divide-y divide-border border border-border">
              {overview.ranked.map((r) => {
                const isActive = r.candidate.id === activeId;
                return (
                  <li key={r.candidate.id}>
                    <button
                      onClick={() => setActiveId(r.candidate.id)}
                      className={`w-full text-left p-3 flex items-center gap-3 ${
                        isActive ? 'bg-foreground text-background' : 'hover:bg-secondary'
                      }`}
                      data-testid={`sandbox-row-${r.candidate.id}`}
                    >
                      <FitBadge value={r.score.overall_fit} size="sm" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate">
                          {r.candidate.name}
                        </div>
                        <div className={`mono text-[11px] truncate ${isActive ? 'text-accent' : 'text-muted-foreground'}`}>
                          {r.candidate.headline || '—'}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mt-6 mono text-xs text-muted-foreground leading-relaxed">
              In your real account these candidates would be the people who applied to <span className="font-bold text-foreground">your</span> job — ranked by the same engine. Click any name to see why.
            </div>
          </div>

          {/* Right: Why panel */}
          <div className="lg:col-span-8 bg-secondary/40 p-6" data-testid="sandbox-why-panel">
            {!why ? (
              <div className="mono text-sm text-muted-foreground p-8 text-center">
                Loading why…
              </div>
            ) : (
              <div className="bg-white border border-border p-6">
                <div className="flex items-start gap-6 flex-wrap">
                  <FitBadge value={why.score.overall_fit} size="lg" />
                  <div className="flex-1 min-w-[200px]">
                    <div className="label-eyebrow text-primary mb-2 flex items-center gap-1">
                      <Sparkle size={12} weight="fill" /> WHY THIS CANDIDATE
                    </div>
                    <h2
                      className="font-display font-black text-2xl lg:text-3xl tracking-tighter leading-tight mb-2"
                      data-testid="sandbox-why-headline"
                    >
                      {active?.candidate.name}
                      <span className="block text-muted-foreground text-base font-semibold mt-1">
                        for {job.title}
                      </span>
                    </h2>
                    <p className="mono text-xs text-muted-foreground">{why.tldr}</p>
                  </div>
                </div>

                {why.score.explanation && (
                  <blockquote className="mt-5 border-l-4 border-primary pl-4 py-2 text-sm leading-relaxed">
                    {why.score.explanation}
                  </blockquote>
                )}

                <div className="grid md:grid-cols-2 gap-6 mt-6 pt-5 border-t border-border">
                  <div>
                    <div className="label-eyebrow text-muted-foreground mb-2 flex items-center gap-1">
                      <ChartBar size={11} /> SCORE BREAKDOWN
                    </div>
                    <ScoreRadar scores={why.score} />
                  </div>
                  <div>
                    <div className="label-eyebrow text-muted-foreground mb-2">COMPONENTS</div>
                    <ScoreBars scores={why.score} />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-6 pt-5 border-t border-border">
                  <div>
                    <div className="label-eyebrow text-muted-foreground mb-2 flex items-center gap-1">
                      <Target size={11} /> MATCHED · {why.matched_signals.length}
                    </div>
                    {why.matched_signals.length === 0 ? (
                      <div className="mono text-xs text-muted-foreground">No direct matches.</div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {why.matched_signals.map((s, i) => (
                          <span
                            key={`${s.kind}-${s.label}-${i}`}
                            className={`label-eyebrow px-2 py-1 ${SIGNAL_STYLE[s.kind] || 'bg-secondary text-foreground'}`}
                          >
                            {s.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="label-eyebrow text-muted-foreground mb-2 flex items-center gap-1">
                      <Warning size={11} /> GAPS · {why.missing_skills.length}
                    </div>
                    {why.missing_skills.length === 0 ? (
                      <div className="mono text-xs text-muted-foreground">All requirements met.</div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {why.missing_skills.map((s) => (
                          <span
                            key={s}
                            className="label-eyebrow px-2 py-1 bg-muted text-muted-foreground border border-dashed border-border"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {why.proof_points.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-border">
                    <div className="label-eyebrow text-muted-foreground mb-2 flex items-center gap-1">
                      <Lightbulb size={11} /> PROOF POINTS
                    </div>
                    <div className="grid md:grid-cols-3 gap-2">
                      {why.proof_points.map((p) => (
                        <div key={p.id} className="border border-border bg-secondary/30 overflow-hidden">
                          <ProjectMedia project={p} aspect="16/9" />
                          <div className="p-2">
                            <div className="font-medium text-xs leading-tight">{p.title}</div>
                            {p.matched_skills?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {p.matched_skills.map((s) => (
                                  <span
                                    key={s}
                                    className="label-eyebrow bg-primary/10 text-primary px-1.5 py-0.5"
                                  >
                                    {s}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {why.top_credentials.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-border">
                    <div className="label-eyebrow text-muted-foreground mb-2 flex items-center gap-1">
                      <ShieldCheck size={11} /> CREDENTIALS
                    </div>
                    <ul className="space-y-1">
                      {why.top_credentials.map((c, i) => (
                        <li key={i} className="text-xs">
                          <span className="font-medium">{c.title}</span>{' '}
                          <span className="text-muted-foreground">— {c.issuer}, {c.year}</span>
                          {c.verified && <span className="ml-2 label-eyebrow text-primary">VERIFIED</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom conversion */}
        <div className="bg-foreground text-background p-8 mt-8 text-center" data-testid="sandbox-bottom-cta">
          <div className="label-eyebrow text-accent mb-2">READY?</div>
          <div className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-4">
            Post a real role. Get a ranked shortlist by Friday.
          </div>
          <p className="text-sm text-background/80 max-w-xl mx-auto mb-6">
            Free until your first hire. 74 role families ready. No credit card required to post.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Link to="/register">
              <Button
                className="rounded-none bg-accent text-foreground hover:bg-accent/90"
                data-testid="sandbox-cta-register"
              >
                Create my employer account
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                variant="outline"
                className="rounded-none bg-transparent border-background/40 text-background hover:bg-background hover:text-foreground"
              >
                See pricing
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sandbox;
